import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
interface JustificativasSectionProps {
  scores: Record<string, Record<string, any>>;
  attributes: string[];
  mainBrand: string;
  findCompetitorUrl?: (brand: string) => string;
  getFavicon?: (url: string) => string;
}
const JustificativasSection: React.FC<JustificativasSectionProps> = ({
  scores,
  attributes,
  mainBrand,
  findCompetitorUrl,
  getFavicon
}) => {
  const [selectedAttribute, setSelectedAttribute] = useState<string>(attributes.length > 0 ? attributes[0] : '');
  const [expandedBrands, setExpandedBrands] = useState<Record<string, boolean>>({});
  // Reset expanded brands when attribute changes
  useEffect(() => {
    setExpandedBrands({});
  }, [selectedAttribute]);
  // Check if we have the new format with justifications
  const hasJustificativas = () => {
    for (const brand in scores) {
      const attrValue = scores[brand][selectedAttribute];
      if (typeof attrValue === 'object' && attrValue !== null && 'justificativa' in attrValue) {
        return true;
      }
    }
    return false;
  };
  // Get score value safely (handles both old and new format)
  const getScore = (brand: string, attribute: string) => {
    const value = scores[brand][attribute];
    if (typeof value === 'number') {
      return value;
    } else if (typeof value === 'object' && value !== null && 'score' in value) {
      return value.score;
    }
    return 0;
  };
  // Get justification safely (only in new format)
  const getJustificativa = (brand: string, attribute: string) => {
    const value = scores[brand][attribute];
    if (typeof value === 'object' && value !== null && 'justificativa' in value) {
      return value.justificativa;
    }
    return null;
  };
  // Get evidences safely (only in new format)
  const getEvidencias = (brand: string, attribute: string) => {
    const value = scores[brand][attribute];
    if (typeof value === 'object' && value !== null && 'evidencias' in value && Array.isArray(value.evidencias)) {
      return value.evidencias;
    }
    return [];
  };
  // Toggle brand expansion
  const toggleBrandExpansion = (brand: string) => {
    setExpandedBrands(prev => ({
      ...prev,
      [brand]: !prev[brand]
    }));
  };
  // Sort brands by score for the selected attribute
  const sortedBrands = Object.keys(scores).sort((a, b) => {
    return getScore(b, selectedAttribute) - getScore(a, selectedAttribute);
  });
  return <div className="w-full" data-id="element-742">
      <h2 className="text-xl sm:text-2xl font-bold mb-3 text-gray-900" data-id="element-743">
        Justificativas das Notas
      </h2>
      <p className="text-gray-800 mb-6" data-id="element-744">
        Explicações detalhadas sobre os critérios utilizados na avaliação e as
        evidências que fundamentam cada nota.
      </p>
      {/* Attribute selector */}
      <div className="mb-6" data-id="element-745">
        <label htmlFor="justificativa-attribute-select" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-746">
          Selecione um atributo para ver as justificativas:
        </label>
        <div className="relative inline-block w-full max-w-xs" data-id="element-747">
          <select id="justificativa-attribute-select" value={selectedAttribute} onChange={e => setSelectedAttribute(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-[#1649ff] focus:border-[#1649ff] sm:text-sm rounded-md appearance-none shadow-sm text-gray-900" data-id="element-748">
            {attributes.map(attribute => <option key={attribute} value={attribute} data-id="element-749">
                {attribute}
              </option>)}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-750">
            <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-751" />
          </div>
        </div>
      </div>
      {/* No justifications message */}
      {!hasJustificativas() && <div className="p-4 bg-yellow-50 rounded-md text-yellow-700 mb-6" data-id="element-752">
          <p className="font-medium" data-id="element-753">Formato Legado</p>
          <p data-id="element-754">
            Esta análise foi realizada antes de 08/08/2025 e não contém
            justificativas detalhadas. As análises realizadas após esta data
            incluem justificativas e evidências para cada nota.
          </p>
        </div>}
      {/* Justifications section */}
      <div className="space-y-6" data-id="element-755">
        {sortedBrands.map(brand => {
        const score = getScore(brand, selectedAttribute);
        const justificativa = getJustificativa(brand, selectedAttribute);
        const evidencias = getEvidencias(brand, selectedAttribute);
        const isExpanded = !!expandedBrands[brand];
        return <div key={brand} className={`border rounded-lg overflow-hidden ${brand === mainBrand ? 'border-[#1649ff] bg-blue-50' : 'border-gray-200'}`} data-id="element-756">
              {/* Brand header with score */}
              <div className={`flex items-center justify-between p-4 cursor-pointer ${brand === mainBrand ? 'bg-blue-100' : 'bg-gray-50'}`} onClick={() => toggleBrandExpansion(brand)} data-id="element-757">
                <div className="flex items-center" data-id="element-758">
                  {findCompetitorUrl && getFavicon && <img src={getFavicon(findCompetitorUrl(brand))} alt={`${brand} favicon`} className="w-5 h-5 mr-2" onError={e => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = 'https://www.google.com/s2/favicons?domain=example.com&sz=128';
              }} data-id="element-759" />}
                  <h3 className="font-medium text-gray-900" data-id="element-760">{brand}</h3>
                  <div className="ml-3 px-2 py-1 bg-white rounded text-sm font-bold text-gray-900 border border-gray-200" data-id="element-761">
                    {score}
                  </div>
                </div>
                <button className="text-gray-500 hover:text-gray-700" aria-label={isExpanded ? 'Recolher' : 'Expandir'} data-id="element-762">
                  {isExpanded ? <ChevronUp size={20} data-id="element-763" /> : <ChevronDown size={20} data-id="element-764" />}
                </button>
              </div>
              {/* Expanded content with justification and evidence */}
              {isExpanded && <div className="p-4 border-t border-gray-200" data-id="element-765">
                  {hasJustificativas() ? <>
                      {/* Justificativa section - always shown */}
                      <div className="mb-4" data-id="element-766">
                        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-767">
                          Justificativa:
                        </h4>
                        {justificativa ? <p className="text-gray-900" data-id="element-768">{justificativa}</p> : <p className="text-gray-500 italic" data-id="element-769">
                            Não há justificativa disponível para esta marca e
                            atributo.
                          </p>}
                      </div>
                      {/* Evidências section - always shown */}
                      <div data-id="element-770">
                        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-771">
                          Evidências:
                        </h4>
                        {evidencias.length > 0 ? <ul className="list-disc pl-5 space-y-1 text-gray-900" data-id="element-772">
                            {evidencias.map((evidencia, index) => <li key={index} data-id="element-773">{evidencia}</li>)}
                          </ul> : <p className="text-gray-500 italic" data-id="element-774">
                            Não há evidências disponíveis para esta marca e
                            atributo.
                          </p>}
                      </div>
                    </> : <p className="text-gray-500 italic" data-id="element-775">
                      Não há justificativas detalhadas disponíveis para esta
                      marca e atributo.
                    </p>}
                </div>}
            </div>;
      })}
      </div>
    </div>;
};
export default JustificativasSection;